/**
 * LISTE BLANCHE du simulateur `Time.deltaTime` — source de vérité unique.
 * Tout ce qui n'est pas ici est refusé avec une erreur `unsupported` (symbole exact + message FR).
 * Le README du dossier reprend cette liste ; garder les deux synchronisés.
 */

import type { SimType } from './ast';

export const ALLOWED_USINGS: ReadonlySet<string> = new Set([
  'UnityEngine',
  'System',
  'System.Collections',
  'System.Collections.Generic',
]);

/** Types de champs / variables locales acceptés. */
export const VALUE_TYPES: Readonly<Record<string, SimType>> = {
  float: 'float',
  int: 'int',
  bool: 'bool',
  Vector2: 'Vector2',
  Vector3: 'Vector3',
};

export const LIFECYCLE_METHODS = ['Awake', 'Start', 'Update'] as const;

/** Touches reconnues par `Input.GetKey/GetKeyDown/GetKeyUp`. Z/Q ajoutés pour les claviers AZERTY. */
export const ALLOWED_KEYCODES = [
  'LeftArrow', 'RightArrow', 'UpArrow', 'DownArrow',
  'W', 'A', 'S', 'D', 'Z', 'Q',
  'Space',
] as const;
export type KeyCode = (typeof ALLOWED_KEYCODES)[number];
export const KEYCODE_SET: ReadonlySet<string> = new Set(ALLOWED_KEYCODES);

export const ALLOWED_AXES: ReadonlySet<string> = new Set(['Horizontal', 'Vertical']);

/** Membres de `transform` : `position` (lecture/écriture), `Translate(Vector3)` / `Translate(x, y, z)`. */
export const TRANSFORM_PROPERTIES: Readonly<Record<string, SimType>> = { position: 'Vector3' };
export const TRANSFORM_METHODS: ReadonlySet<string> = new Set(['Translate']);

export const TIME_PROPERTIES: Readonly<Record<string, SimType>> = {
  deltaTime: 'float',
  time: 'float',
  frameCount: 'int',
};

export const INPUT_METHODS: ReadonlySet<string> = new Set(['GetAxis', 'GetAxisRaw', 'GetKey', 'GetKeyDown', 'GetKeyUp']);

/** `Mathf.X(...)` : arité attendue. Retour float (ou int si tous les arguments sont des int pour Abs/Min/Max/Clamp). */
export const MATHF_FUNCTIONS: Readonly<Record<string, number>> = {
  Clamp: 3,
  Clamp01: 1,
  Abs: 1,
  Min: 2,
  Max: 2,
  Sqrt: 1,
  Sin: 1,
  Cos: 1,
  Floor: 1,
  Ceil: 1,
  Round: 1,
  Lerp: 3,
  Sign: 1,
};
export const MATHF_INT_PRESERVING: ReadonlySet<string> = new Set(['Abs', 'Min', 'Max', 'Clamp']);
export const MATHF_CONSTANTS: Readonly<Record<string, number>> = {
  PI: Math.PI,
  Deg2Rad: Math.PI / 180,
  Rad2Deg: 180 / Math.PI,
};

export const VECTOR3_STATICS: readonly string[] = ['zero', 'one', 'up', 'down', 'left', 'right', 'forward', 'back'];
export const VECTOR2_STATICS: readonly string[] = ['zero', 'one', 'up', 'down', 'left', 'right'];
/** `Vector3.X(a, b)` / `Vector2.X(a, b)` : arité + type de retour ('self' = le type du vecteur). */
export const VECTOR_FUNCTIONS: Readonly<Record<string, { arity: number; returns: 'self' | 'float' }>> = {
  ClampMagnitude: { arity: 2, returns: 'self' },
  Distance: { arity: 2, returns: 'float' },
  Dot: { arity: 2, returns: 'float' },
};

/** Membres d'instance des vecteurs (lecture). `x/y/z` sont aussi assignables sur une variable. */
export const VECTOR_INSTANCE_MEMBERS: Readonly<Record<string, 'float' | 'self'>> = {
  x: 'float',
  y: 'float',
  z: 'float', // refusé sur Vector2 par l'analyseur
  magnitude: 'float',
  sqrMagnitude: 'float',
  normalized: 'self',
};

export const DEBUG_METHODS: ReadonlySet<string> = new Set(['Log', 'LogWarning', 'LogError']);

/** Identifiants « racine » réservés à l'API : ne peuvent pas être utilisés seuls ni redéclarés. */
export const API_ROOTS: ReadonlySet<string> = new Set(['transform', 'Time', 'Input', 'Mathf', 'Vector2', 'Vector3', 'KeyCode', 'Debug']);

export interface UnsupportedInfo {
  message: string;
  hint?: string;
}

/**
 * Symboles connus, volontairement HORS périmètre, avec un message pédagogique.
 * Clé = symbole tel que l'étudiant l'écrit (identifiant seul ou `Racine.membre`).
 */
export const KNOWN_UNSUPPORTED: Readonly<Record<string, UnsupportedInfo>> = {
  // Coroutines
  StartCoroutine: { message: 'Les coroutines ne sont pas au programme de cet exercice : ici, tout se passe dans `Update()`, image par image.', hint: 'Déplace ton objet directement dans `Update()` avec `Time.deltaTime`.' },
  StopCoroutine: { message: 'Les coroutines ne sont pas au programme de cet exercice.' },
  StopAllCoroutines: { message: 'Les coroutines ne sont pas au programme de cet exercice.' },
  IEnumerator: { message: 'Les coroutines (`IEnumerator` / `yield`) ne sont pas au programme de cet exercice : le déplacement se fait dans `Update()`.', hint: 'Supprime la coroutine et écris le déplacement dans `Update()`.' },
  yield: { message: '`yield` appartient aux coroutines, qui ne sont pas au programme ici.' },
  WaitForSeconds: { message: 'Les coroutines ne sont pas au programme de cet exercice.' },
  Coroutine: { message: 'Les coroutines ne sont pas au programme de cet exercice.' },
  // Physique
  Rigidbody: { message: 'La physique (`Rigidbody`) n\'est pas au programme de cet exercice : on déplace l\'objet directement via `transform.position`.', hint: 'Utilise `transform.position` ou `transform.Translate(...)` avec `Time.deltaTime`.' },
  Rigidbody2D: { message: 'La physique (`Rigidbody2D`) n\'est pas au programme de cet exercice : on déplace l\'objet directement via `transform.position`.', hint: 'Utilise `transform.position` ou `transform.Translate(...)` avec `Time.deltaTime`.' },
  Physics: { message: 'La physique n\'est pas au programme de cet exercice.' },
  Physics2D: { message: 'La physique n\'est pas au programme de cet exercice.' },
  Collider: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  Collider2D: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  Collision: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  CharacterController: { message: '`CharacterController` n\'est pas au programme de cet exercice.' },
  // Composants / objets
  GetComponent: { message: 'L\'accès aux composants (`GetComponent`) n\'est pas au programme de cet exercice : seul `transform` est disponible, sans le chercher.', hint: 'Écris directement `transform.position`.' },
  gameObject: { message: '`gameObject` n\'est pas au programme de cet exercice : seul `transform` est disponible.' },
  GameObject: { message: '`GameObject` n\'est pas au programme de cet exercice.' },
  Instantiate: { message: 'La création d\'objets (`Instantiate`) n\'est pas au programme de cet exercice.' },
  Destroy: { message: 'La destruction d\'objets (`Destroy`) n\'est pas au programme de cet exercice.' },
  Transform: { message: 'Déclarer un `Transform` n\'est pas nécessaire ici : utilise directement `transform` (minuscule), déjà fourni par MonoBehaviour.' },
  Camera: { message: '`Camera` n\'est pas au programme de cet exercice.' },
  Random: { message: '`Random` n\'est pas au programme de cet exercice : la simulation doit rester déterministe.' },
  Invoke: { message: '`Invoke` n\'est pas au programme de cet exercice.' },
  InvokeRepeating: { message: '`InvokeRepeating` n\'est pas au programme de cet exercice.' },
  Quaternion: { message: 'La rotation (`Quaternion`) n\'est pas au programme de cet exercice : on ne travaille que la position.' },
  Color: { message: '`Color` n\'est pas au programme de cet exercice.' },
  SceneManager: { message: 'La gestion des scènes n\'est pas au programme de cet exercice.' },
  // Types / collections
  List: { message: 'Les listes (`List<>`) ne sont pas au programme de cet exercice : seuls `float`, `int`, `bool`, `Vector2`, `Vector3` sont disponibles.' },
  Dictionary: { message: 'Les dictionnaires ne sont pas au programme de cet exercice.' },
  string: { message: 'Le type `string` n\'est pas au programme de cet exercice (une chaîne n\'est acceptée que dans `Debug.Log(...)`).', hint: 'Seuls `float`, `int`, `bool`, `Vector2`, `Vector3` sont disponibles.' },
  var: { message: '`var` est refusé ici : écris le type explicitement pour bien voir la différence entre `int`, `float` et `Vector3`.', hint: 'Exemple : `float h = Input.GetAxisRaw("Horizontal");`' },
  double: { message: 'Le type `double` n\'est pas utilisé dans Unity pour le gameplay : utilise `float` (avec le suffixe `f` sur les littéraux).' },
  object: { message: 'Le type `object` n\'est pas au programme de cet exercice.' },
  // Contrôle
  foreach: { message: '`foreach` n\'est pas au programme de cet exercice (il n\'y a pas de collection à parcourir).', hint: 'Si tu as besoin d\'une boucle, `for` et `while` sont acceptés.' },
  switch: { message: '`switch` n\'est pas au programme de cet exercice.', hint: 'Utilise `if / else if / else`.' },
  do: { message: '`do … while` n\'est pas au programme de cet exercice.', hint: 'Utilise `while` ou `for`.' },
  try: { message: 'La gestion d\'exceptions (`try / catch`) n\'est pas au programme de cet exercice.' },
  throw: { message: 'La gestion d\'exceptions n\'est pas au programme de cet exercice.' },
  goto: { message: '`goto` n\'est pas au programme (et déconseillé en général).' },
  lock: { message: '`lock` n\'est pas au programme de cet exercice.' },
  static: { message: '`static` n\'est pas au programme de cet exercice : tout reste dans l\'instance du MonoBehaviour.' },
  namespace: { message: 'Pas de `namespace` ici : écris directement la classe.' },
  // Méthodes du cycle de vie hors périmètre
  FixedUpdate: { message: '`FixedUpdate()` est hors périmètre ici : cet exercice vise `Update()` et `Time.deltaTime`, et il n\'y a pas de physique.', hint: 'Écris ton déplacement dans `Update()`.' },
  LateUpdate: { message: '`LateUpdate()` n\'est pas au programme de cet exercice : utilise `Update()`.' },
  OnEnable: { message: '`OnEnable()` n\'est pas au programme de cet exercice.' },
  OnDisable: { message: '`OnDisable()` n\'est pas au programme de cet exercice.' },
  OnDestroy: { message: '`OnDestroy()` n\'est pas au programme de cet exercice.' },
  OnCollisionEnter: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  OnCollisionEnter2D: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  OnTriggerEnter: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  OnTriggerEnter2D: { message: 'Les collisions ne sont pas au programme de cet exercice.' },
  OnGUI: { message: '`OnGUI()` n\'est pas au programme de cet exercice.' },
  // Membres d'API hors périmètre
  'transform.localScale': { message: 'L\'échelle (`localScale`) n\'est pas au programme de cet exercice : on ne travaille que `transform.position`.' },
  'transform.rotation': { message: 'La rotation n\'est pas au programme de cet exercice : on ne travaille que `transform.position`.' },
  'transform.localPosition': { message: 'Utilise `transform.position` : il n\'y a pas de parent dans cette scène.' },
  'transform.eulerAngles': { message: 'La rotation n\'est pas au programme de cet exercice.' },
  'transform.Rotate': { message: 'La rotation (`transform.Rotate`) n\'est pas au programme de cet exercice : on ne travaille que la position.' },
  'transform.forward': { message: '`transform.forward` dépend de la rotation, hors périmètre ici.', hint: 'Utilise `Vector3.right` / `Vector3.up` ou une direction construite avec `new Vector3(h, v, 0f)`.' },
  'transform.right': { message: '`transform.right` dépend de la rotation, hors périmètre ici.', hint: 'Utilise `Vector3.right`.' },
  'transform.up': { message: '`transform.up` dépend de la rotation, hors périmètre ici.', hint: 'Utilise `Vector3.up`.' },
  'transform.LookAt': { message: 'La rotation n\'est pas au programme de cet exercice.' },
  'Time.fixedDeltaTime': { message: '`Time.fixedDeltaTime` sert à `FixedUpdate()` (physique), hors périmètre ici.', hint: 'Dans `Update()`, la bonne valeur est `Time.deltaTime`.' },
  'Time.timeScale': { message: '`Time.timeScale` n\'est pas au programme de cet exercice.' },
  'Time.unscaledDeltaTime': { message: '`Time.unscaledDeltaTime` n\'est pas au programme de cet exercice : utilise `Time.deltaTime`.' },
  'Time.realtimeSinceStartup': { message: '`Time.realtimeSinceStartup` n\'est pas au programme : utilise `Time.time`.' },
  'Input.GetButton': { message: '`Input.GetButton` n\'est pas au programme de cet exercice.', hint: 'Utilise `Input.GetKey(KeyCode.Space)` ou `Input.GetAxisRaw("Horizontal")`.' },
  'Input.GetButtonDown': { message: '`Input.GetButtonDown` n\'est pas au programme de cet exercice.', hint: 'Utilise `Input.GetKeyDown(KeyCode.Space)`.' },
  'Input.GetButtonUp': { message: '`Input.GetButtonUp` n\'est pas au programme de cet exercice.' },
  'Input.GetMouseButton': { message: 'La souris n\'est pas au programme de cet exercice : seules les flèches / ZQSD / WASD / Espace sont simulées.' },
  'Input.GetMouseButtonDown': { message: 'La souris n\'est pas au programme de cet exercice.' },
  'Input.mousePosition': { message: 'La souris n\'est pas au programme de cet exercice.' },
  'Input.anyKey': { message: '`Input.anyKey` n\'est pas au programme de cet exercice.' },
  'Input.anyKeyDown': { message: '`Input.anyKeyDown` n\'est pas au programme de cet exercice.' },
  'Debug.DrawLine': { message: '`Debug.DrawLine` n\'est pas au programme (rien à dessiner ici).' },
  'Debug.DrawRay': { message: '`Debug.DrawRay` n\'est pas au programme (rien à dessiner ici).' },
};
